/* eslint-disable complexity */
/* eslint-disable @typescript-eslint/init-declarations */
/**
 * @file utils/RequestManager.ts
 * @description 请求管理器，静默处理请求取消
 */

/**
 * 标准响应格式
 */
interface StandardResponse<T = any> {
    code: string | number;
    message: string;
    data: T;
}

/**
 * 执行选项接口
 */
interface ExecuteOptions {
    /**
     * 是否手动触发
     */
    isManual?: boolean;

    /**
     * 请求超时时间（毫秒）
     */
    timeout?: number;
}

/**
 * 请求管理器（单例）
 */
class RequestManager {
    private static instanceIdCounter = 0;
    public readonly instanceId: number;
    private readonly abortControllers = new Map<string, AbortController>();

    constructor() {
        this.instanceId = ++RequestManager.instanceIdCounter;
    }

    /**
     * 创建一个空响应（用于被取消的请求）
     */
    private createEmptyResponse<T = any>(): StandardResponse<T> {
        return {
            code: '0',
            message: '',
            data: {} as T
        };
    }

    /**
     * 执行请求
     * @param key - 请求的唯一标识
     * @param requestFn - 请求函数（接收 AbortSignal）
     * @param options - 执行选项
     */
    async execute<T>(
        key: string,
        requestFn: (signal: AbortSignal) => Promise<T>,
        options?: ExecuteOptions
    ): Promise<T> {
        const {isManual = false, timeout} = options || {};

        // 静默取消旧请求
        const oldController = this.abortControllers.get(key);
        if (oldController) {
            oldController.abort();
        }

        // 创建新的 AbortController
        const controller = new AbortController();
        const currentController = controller;
        this.abortControllers.set(key, controller);

        // 设置超时
        let timeoutId: number | undefined;
        if (timeout) {
            timeoutId = window.setTimeout(() => {
                controller.abort();
            }, timeout);
        }

        try {
            const result = await requestFn(controller.signal);

            // 清除超时
            if (timeoutId) {
                clearTimeout(timeoutId);
            }

            // 只删除当前 controller
            if (this.abortControllers.get(key) === currentController) {
                this.abortControllers.delete(key);
            }

            return result;
        } catch (error) {
            // 清除超时
            if (timeoutId) {
                clearTimeout(timeoutId);
            }

            // 只删除当前 controller
            if (this.abortControllers.get(key) === currentController) {
                this.abortControllers.delete(key);
            }

            // 静默处理取消错误 - 不打印任何日志，返回空响应
            if (error instanceof Error && (error.name === 'AbortError' || error.name === 'CanceledError')) {
                return this.createEmptyResponse() as T;
            }

            // 其他错误继续抛出
            throw error;
        }
    }

    /**
     * 取消指定 key 的请求
     */
    cancel(key: string): void {
        const controller = this.abortControllers.get(key);
        if (controller) {
            controller.abort();
            this.abortControllers.delete(key);
        }
    }

    /**
     * 取消所有请求
     */
    cancelAll(): void {
        this.abortControllers.forEach((controller) => {
            controller.abort();
        });
        this.abortControllers.clear();
    }

    /**
     * 取消包含指定模式的所有请求
     */
    cancelByPattern(pattern: string | RegExp): void {
        const regex = typeof pattern === 'string' ? new RegExp(pattern) : pattern;
        const canceledKeys: string[] = [];

        this.abortControllers.forEach((controller, key) => {
            if (regex.test(key)) {
                controller.abort();
                canceledKeys.push(key);
            }
        });

        canceledKeys.forEach(key => {
            this.abortControllers.delete(key);
        });
    }

    /**
     * 取消指定的多个请求
     */
    cancelByKeys(keys: string[]): void {
        keys.forEach(key => {
            const controller = this.abortControllers.get(key);
            if (controller) {
                controller.abort();
                this.abortControllers.delete(key);
            }
        });
    }

    /**
     * 获取当前活跃的请求数量
     */
    getActiveRequestCount(): number {
        return this.abortControllers.size;
    }

    /**
     * 检查某个 key 的请求是否正在进行
     */
    isRequestActive(key: string): boolean {
        return this.abortControllers.has(key);
    }

    /**
     * 获取所有活跃请求的key列表
     */
    getAllActiveKeys(): string[] {
        return Array.from(this.abortControllers.keys());
    }

    /**
     * 获取包含指定字符串的活跃请求key
     */
    getActiveKeysByPattern(pattern: string | RegExp): string[] {
        const regex = typeof pattern === 'string' ? new RegExp(pattern) : pattern;
        const matchedKeys: string[] = [];
        
        this.abortControllers.forEach((controller, key) => {
            if (regex.test(key)) {
                matchedKeys.push(key);
            }
        });
        
        return matchedKeys;
    }

    /**
     * 调试：打印当前所有活跃请求
     */
    debugActiveRequests(prefix: string = ''): void {
        console.log(`${prefix}[RequestManager Debug] 活跃请求数: ${this.abortControllers.size}`);
        if (this.abortControllers.size > 0) {
            console.log(`${prefix}活跃请求列表:`);
            this.abortControllers.forEach((controller, key) => {
                console.log(`${prefix}  - ${key}`);
            });
        }
    }
}

// 导出单例
export const requestManager = new RequestManager();